import { useState, useEffect, useCallback } from 'react';
import { FlaskConical, Flame, Droplets, Filter, Beaker, ThermometerSun, Check, AlertTriangle } from 'lucide-react';
import LabBench from '../LabBench';
import InfoBox from '../InfoBox';
import { useLab } from '@/context/LabContext';

// Educational content for each step
const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Setting Up the Water Bath',
    content: 'A water bath provides gentle, even heating for temperature-sensitive reactions. The maximum temperature is limited to 100°C (boiling point of water), preventing overheating. This is crucial for saponification, which requires controlled heating to avoid charring the fat.',
    whatHappens: 'The water bath heats to 90-95°C, providing a stable heat source. The evaporating dish sits partially submerged, receiving indirect heat.',
    observe: [
      'Water temperature rises gradually',
      'Small bubbles form as water approaches boiling',
      'Temperature stabilizes near 95°C'
    ]
  },
  2: {
    title: 'Weighing the Lard',
    content: 'Lard is animal fat composed primarily of triglycerides - esters of glycerol with fatty acids (mainly palmitic, stearic, and oleic acids). Accurate weighing is essential because the stoichiometry of saponification requires a specific ratio of fat to base.',
    whatHappens: 'You measure approximately 5.0g of solid lard. The analytical balance ensures precise measurement for yield calculations.',
    observe: [
      'Lard appears as white, solid fat',
      'Mass reading stabilizes at ~5.0g',
      'Record the exact mass for calculations'
    ]
  },
  3: {
    title: 'Transferring to 125 mL Flask',
    content: 'Place about 5.0 g of lard or vegetable oil into a 125 mL flask. The flask must fit inside the large beaker used for the water bath.',
    whatHappens: 'The lard is placed in the flask, ready for the saponification reaction.',
    observe: [
      'Lard sits as a solid mass in the flask',
      'Flask should be clean and dry',
      'Position for even heating in water bath'
    ]
  },
  4: {
    title: 'Adding NaOH/Ethanol Solution',
    content: 'Add 30.0 mL of 15% NaOH alcohol stock solution (CAUTION: highly caustic). Add several boiling chips and cover the flask with a watch glass to avoid splashing. Ethanol serves as a co-solvent, helping to mix the aqueous NaOH with the hydrophobic fat. This homogeneous mixing is critical for the reaction to proceed.',
    whatHappens: 'The NaOH/ethanol solution mixes with the lard. The ethanol dissolves in the fat while carrying dissolved NaOH, enabling the saponification reaction.',
    observe: [
      'Solution mixes with the fat',
      'Mixture may become slightly cloudy',
      'Add boiling chips and cover with watch glass',
      'Stirring helps homogenize the mixture'
    ]
  },
  5: {
    title: 'Saponification Reaction',
    content: 'Saponification is the alkaline hydrolysis of triglycerides:\n\nFat + 3 NaOH → 3 Sodium carboxylate (soap) + Glycerol\n\nThe ester bonds are cleaved by hydroxide ions, producing the sodium salts of fatty acids (soap) and glycerol as a byproduct. Heat the flask by suspending it 2 mm above a bath of boiling water for 40 minutes. As the water level falls, lower the flask so that the bottom always contacts the boiling water.',
    whatHappens: 'The triglycerides are converted to soap molecules. The mixture thickens as soap forms and may become opaque.',
    observe: [
      'Mixture thickens over time',
      'Color may change to cream/white',
      'Consistency becomes more uniform',
      'Reaction takes 40 minutes'
    ]
  },
  6: {
    title: 'Salting Out with NaCl',
    content: 'Dissolve about 25.0 g of NaCl in 150 mL of water in a 400 mL beaker (set ~25 mL aside for rinsing). Pour the hot reaction mixture into the salt solution and rinse the flask with the reserved salt solution. Salting out exploits the common ion effect - excess Na+ ions decrease soap solubility.',
    whatHappens: 'The soap separates from the glycerol/water mixture. The high ionic strength of the salt solution forces the soap out of solution.',
    observe: [
      'Soap precipitates as curds',
      'Clear liquid (glycerol solution) separates',
      'Soap floats or forms distinct layer'
    ]
  },
  7: {
    title: 'Straining the Soap',
    content: 'Stir for several minutes, then strain the mixture through several layers of cheesecloth to separate the solid soap from the liquid phase containing glycerol, excess NaOH, and NaCl.',
    whatHappens: 'The soap collects on the cheesecloth as a solid mass, while the liquid (containing glycerol and salts) passes through.',
    observe: [
      'Soap collects on cheesecloth',
      'Filtrate appears yellowish (glycerol)',
      'Soap may look grainy or chunky'
    ]
  },
  8: {
    title: 'Washing with Ice Water',
    content: 'Washing removes residual NaOH, NaCl, and glycerol from the soap. Ice-cold water is used because soap is less soluble in cold water, minimizing product loss during washing. Complete removal of NaOH is important as it can irritate skin.',
    whatHappens: 'Cold water rinses away water-soluble impurities without dissolving the soap.',
    observe: [
      'Use cold (ice) water',
      'Soap remains solid during wash',
      'Multiple washes may be needed'
    ]
  },
  9: {
    title: 'Collecting the Soap Product',
    content: 'Twist the cheesecloth to remove excess water from the soap. Describe your prepared soap in terms of its appearance, colour, texture, and odour. Record your observations. Set aside for use in Part III (Property Testing).',
    whatHappens: 'The purified soap is collected. It may still contain some water and will become harder as it dries.',
    observe: [
      'Record the appearance, colour, texture, odour',
      'Soap may be soft (contains water)',
      'Color should be off-white to cream',
      'Set aside for Part III testing'
    ]
  }
};

interface Part1SoapMakingProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

export default function Part1SoapMaking({ onStepComplete, currentStep = 1 }: Part1SoapMakingProps) {
  const { state, update } = useLab();
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // Local state for this part
  const [waterBathTemp, setWaterBathTemp] = useState(25);
  const [isHeating, setIsHeating] = useState(false);
  const [lardWeighed, setLardWeighed] = useState(false);
  const [lardMass, setLardMass] = useState(0);
  const [lardInDish, setLardInDish] = useState(false);
  const [naohAdded, setNaohAdded] = useState(false);
  const [reactionProgress, setReactionProgress] = useState(0);
  const [saltedOut, setSaltedOut] = useState(false);
  const [filtered, setFiltered] = useState(false);
  const [washed, setWashed] = useState(false);
  const [soapCollected, setSoapCollected] = useState(false);
  const [soapMass, setSoapMass] = useState(0);

  // Heating simulation
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isHeating && waterBathTemp < 95) {
      interval = setInterval(() => {
        setWaterBathTemp(prev => Math.min(prev + 2, 95));
      }, 200);
    }
    return () => clearInterval(interval);
  }, [isHeating, waterBathTemp]);

  // Saponification reaction simulation
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (naohAdded && waterBathTemp >= 90 && reactionProgress < 100) {
      interval = setInterval(() => {
        setReactionProgress(prev => Math.min(prev + 0.5, 100));
      }, 200);
    }
    return () => clearInterval(interval);
  }, [naohAdded, waterBathTemp, reactionProgress]);

  const handleSetupWaterBath = useCallback(() => {
    setIsHeating(true);
    setTimeout(() => {
      if (onStepComplete) onStepComplete('soapMaking-1');
    }, 3000);
  }, [onStepComplete]);

  const handleWeighLard = useCallback(() => {
    const mass = 4.95 + Math.random() * 0.1; // 4.95-5.05g
    setLardMass(parseFloat(mass.toFixed(2)));
    setLardWeighed(true);
    if (onStepComplete) onStepComplete('soapMaking-2');
  }, [onStepComplete]);

  const handleTransferLard = useCallback(() => {
    if (lardWeighed) {
      setLardInDish(true);
      if (onStepComplete) onStepComplete('soapMaking-3');
    }
  }, [lardWeighed, onStepComplete]);

  const handleAddNaOH = useCallback(() => {
    if (lardInDish) {
      setNaohAdded(true);
      if (onStepComplete) onStepComplete('soapMaking-4');
    }
  }, [lardInDish, onStepComplete]);

  const handleHeatReaction = useCallback(() => {
    if (naohAdded && waterBathTemp >= 90) {
      // Wait for reaction to complete
      const checkComplete = setInterval(() => {
        if (reactionProgress >= 100) {
          clearInterval(checkComplete);
          if (onStepComplete) onStepComplete('soapMaking-5');
        }
      }, 500);
    }
  }, [naohAdded, waterBathTemp, reactionProgress, onStepComplete]);

  const handleSaltOut = useCallback(() => {
    if (reactionProgress >= 100) {
      setSaltedOut(true);
      if (onStepComplete) onStepComplete('soapMaking-6');
    }
  }, [reactionProgress, onStepComplete]);

  const handleFilter = useCallback(() => {
    if (saltedOut) {
      setFiltered(true);
      if (onStepComplete) onStepComplete('soapMaking-7');
    }
  }, [saltedOut, onStepComplete]);

  const handleWash = useCallback(() => {
    if (filtered) {
      setWashed(true);
      if (onStepComplete) onStepComplete('soapMaking-8');
    }
  }, [filtered, onStepComplete]);

  const handleCollectSoap = useCallback(() => {
    if (washed) {
      const mass = 4.2 + Math.random() * 0.6; // 4.2-4.8g
      setSoapMass(parseFloat(mass.toFixed(2)));
      setSoapCollected(true);
      if (onStepComplete) onStepComplete('soapMaking-9');
    }
  }, [washed, onStepComplete]);

  // Calculate soap color based on reaction progress
  const getSoapColor = () => {
    if (reactionProgress < 30) return '#FFF8DC'; // Cream (melted lard)
    if (reactionProgress < 70) return '#F5DEB3'; // Wheat (saponifying)
    return '#FFFAF0';  // Floral white (soap)
  };

  return (
    <LabBench
      experimentName="Soap Making"
      completedCount={[lardWeighed, lardInDish, naohAdded, reactionProgress >= 100, saltedOut, filtered, washed, soapCollected].filter(Boolean).length}
      totalSteps={9}
      equipment={['Water bath', '125 mL flask', 'Watch glass', 'Boiling chips', 'Cheesecloth']}
      labMode="guided"
      currentStep={currentStep}
    >
      <div className="soap-making-lab">
        {/* Equipment Area */}
        <div className="equipment-row">
          {/* Water Bath */}
          <div className="equipment-item water-bath">
            <svg viewBox="0 0 120 100" width={120} height={100}>
              {/* Container */}
              <rect x="10" y="30" width="100" height="60" fill="#3A3A3A" rx="5" />
              <rect x="15" y="35" width="90" height="50" fill="#1A3A5A" rx="3" />

              {/* Water */}
              <rect x="18" y="40" width="84" height="42" fill="#4AA8D8" opacity="0.7">
                {isHeating && (
                  <animate attributeName="opacity" values="0.7;0.5;0.7" dur="1s" repeatCount="indefinite" />
                )}
              </rect>

              {/* Bubbles when hot */}
              {waterBathTemp > 80 && (
                <g>
                  <circle cx="40" cy="70" r="2" fill="white" opacity="0.5">
                    <animate attributeName="cy" values="70;45;70" dur="1s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.5;0;0.5" dur="1s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="70" cy="65" r="2" fill="white" opacity="0.5">
                    <animate attributeName="cy" values="65;40;65" dur="0.8s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.5;0;0.5" dur="0.8s" repeatCount="indefinite" />
                  </circle>
                </g>
              )}

              {/* Evaporating dish in water bath */}
              {lardInDish && (
                <g>
                  <ellipse cx="60" cy="55" rx="25" ry="8" fill="rgba(200,220,240,0.5)" stroke="#7A9BB8" strokeWidth="1" />
                  <ellipse cx="60" cy="55" rx="20" ry="5" fill={getSoapColor()} />
                </g>
              )}

              {/* Temperature indicator */}
              <rect x="95" y="40" width="8" height="35" fill="#2A2A2A" rx="2" />
              <rect
                x="97"
                y={75 - (waterBathTemp / 100) * 30}
                width="4"
                height={(waterBathTemp / 100) * 30}
                fill={waterBathTemp > 80 ? '#FF5A36' : '#00A86B'}
              />
            </svg>
            <div className="temp-display">
              <ThermometerSun size={14} />
              <span>{waterBathTemp}°C</span>
            </div>
            <span className="label">Water Bath</span>
          </div>

          {/* Analytical Balance */}
          <div className="equipment-item balance">
            <svg viewBox="0 0 100 80" width={100} height={80}>
              <rect x="10" y="50" width="80" height="25" fill="#3A3A3A" rx="3" />
              <rect x="20" y="30" width="60" height="25" fill="#1A1A1A" rx="2" />
              <rect x="25" y="35" width="50" height="15" fill="#001a00" rx="1" />
              <text x="50" y="47" fontSize="10" fill="#00FF00" textAnchor="middle" fontFamily="monospace">
                {lardWeighed ? `${lardMass}g` : '0.00g'}
              </text>
            </svg>
            <span className="label">Balance</span>
          </div>

          {/* Reagent Bottles */}
          <div className="equipment-item reagents">
            <div className="reagent-bottle" onClick={handleAddNaOH} style={{ cursor: lardInDish && !naohAdded ? 'pointer' : 'default' }}>
              <svg viewBox="0 0 40 60" width={40} height={60}>
                <rect x="10" y="20" width="20" height="35" fill="rgba(200,220,240,0.5)" rx="3" />
                <rect x="14" y="25" width="12" height="25" fill="#FFF8DC" opacity="0.8" />
                <rect x="12" y="10" width="16" height="12" fill="#4A4A4A" rx="2" />
              </svg>
              <span className="reagent-label">NaOH/EtOH</span>
            </div>
            <div className="reagent-bottle">
              <svg viewBox="0 0 40 60" width={40} height={60}>
                <rect x="10" y="20" width="20" height="35" fill="rgba(200,220,240,0.5)" rx="3" />
                <rect x="14" y="30" width="12" height="20" fill="#FFFFFF" opacity="0.8" />
                <rect x="12" y="10" width="16" height="12" fill="#4A4A4A" rx="2" />
              </svg>
              <span className="reagent-label">NaCl (sat)</span>
            </div>
          </div>
        </div>

        {/* Reaction Progress */}
        {naohAdded && (
          <div className="reaction-progress">
            <div className="progress-label">
              <Flame size={14} className={waterBathTemp >= 90 ? 'active' : ''} />
              <span>Saponification Progress</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${reactionProgress}%` }} />
            </div>
            <span className="progress-percent">{Math.round(reactionProgress)}%</span>
            {reactionProgress >= 100 && (
              <span className="complete-badge">
                <Check size={12} /> Complete
              </span>
            )}
          </div>
        )}

        {/* Filtration Area */}
        {saltedOut && (
          <div className="filtration-area">
            <svg viewBox="0 0 100 120" width={100} height={120}>
              {/* Filter funnel */}
              <polygon points="25,20 75,20 60,80 40,80" fill="rgba(200,220,240,0.4)" stroke="#7A9BB8" strokeWidth="1" />
              {/* Filter paper */}
              <polygon points="30,25 70,25 58,75 42,75" fill="white" />
              {/* Precipitate (soap) */}
              {filtered && (
                <ellipse cx="50" cy="50" rx="15" ry="8" fill="#FFFAF0" />
              )}
              {/* Beaker below */}
              <rect x="35" y="82" width="30" height="35" fill="rgba(200,220,240,0.3)" rx="2" />
              <rect x="38" y="95" width="24" height="18" fill="#F5F5DC" opacity="0.5" />
            </svg>
            <span className="label">Filtration</span>
          </div>
        )}

        {/* Final Soap Product */}
        {soapCollected && (
          <div className="soap-product">
            <div className="soap-display">
              <svg viewBox="0 0 80 60" width={80} height={60}>
                <ellipse cx="40" cy="35" rx="30" ry="15" fill="#FFFAF0" stroke="#DDD" strokeWidth="1" />
                <ellipse cx="40" cy="32" rx="25" ry="10" fill="#FFFFF0" opacity="0.5" />
              </svg>
              <div className="soap-info">
                <span className="soap-mass">{soapMass}g</span>
                <span className="soap-label">Sodium Stearate</span>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons">
          {!isHeating && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleSetupWaterBath}>
                <Flame size={16} />
                Heat Water Bath
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(1)}>?</button>
            </div>
          )}
          {!lardWeighed && isHeating && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleWeighLard}>
                <Beaker size={16} />
                Weigh Lard (5.0g)
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(2)}>?</button>
            </div>
          )}
          {lardWeighed && !lardInDish && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleTransferLard}>
                <Droplets size={16} />
                Transfer to Flask
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(3)}>?</button>
            </div>
          )}
          {lardInDish && !naohAdded && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleAddNaOH}>
                <Droplets size={16} />
                Add 30 mL NaOH/EtOH
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(4)}>?</button>
            </div>
          )}
          {naohAdded && reactionProgress < 100 && waterBathTemp >= 90 && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleHeatReaction}>
                <Flame size={16} />
                Heat 40 min
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(5)}>?</button>
            </div>
          )}
          {reactionProgress >= 100 && !saltedOut && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleSaltOut}>
                <FlaskConical size={16} />
                Pour into NaCl Solution
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(6)}>?</button>
            </div>
          )}
          {saltedOut && !filtered && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleFilter}>
                <Filter size={16} />
                Strain through Cheesecloth
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(7)}>?</button>
            </div>
          )}
          {filtered && !washed && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleWash}>
                <Droplets size={16} />
                Wash with Ice Water
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(8)}>?</button>
            </div>
          )}
          {washed && !soapCollected && (
            <div className="action-btn-group">
              <button className="action-btn success" onClick={handleCollectSoap}>
                <Check size={16} />
                Collect Soap
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(9)}>?</button>
            </div>
          )}
        </div>

        {/* Safety Warning */}
        {naohAdded && reactionProgress < 50 && (
          <div className="safety-warning">
            <AlertTriangle size={16} />
            <span>Caution: NaOH is caustic. Avoid skin contact.</span>
          </div>
        )}

        <style>{`
          .soap-making-lab {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 40px;
            align-items: flex-end;
            flex-wrap: wrap;
            justify-content: center;
          }

          .equipment-item {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .equipment-item .label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .temp-display {
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 12px;
            color: var(--text-primary);
            background: var(--bg-elevated);
            padding: 4px 8px;
            border-radius: 4px;
          }

          .reagents {
            display: flex;
            gap: 16px;
          }

          .reagent-bottle {
            display: flex;
            flex-direction: column;
            align-items: center;
            transition: transform 0.2s;
          }

          .reagent-bottle:hover {
            transform: scale(1.05);
          }

          .reagent-label {
            font-size: 9px;
            color: var(--text-muted);
            margin-top: 4px;
          }

          .reaction-progress {
            display: flex;
            align-items: center;
            gap: 12px;
            background: var(--bg-elevated);
            padding: 12px 20px;
            border-radius: 8px;
            width: 100%;
            max-width: 400px;
          }

          .progress-label {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .progress-label svg.active {
            color: #FF5A36;
            animation: pulse 1s infinite;
          }

          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }

          .progress-bar {
            flex: 1;
            height: 8px;
            background: var(--bg-dark);
            border-radius: 4px;
            overflow: hidden;
          }

          .progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #FFD700, #FFA500);
            transition: width 0.3s;
          }

          .progress-percent {
            font-size: 12px;
            color: var(--text-primary);
            min-width: 40px;
          }

          .complete-badge {
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 11px;
            color: #00A86B;
            background: rgba(0, 168, 107, 0.1);
            padding: 4px 8px;
            border-radius: 12px;
          }

          .filtration-area {
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .soap-product {
            background: var(--bg-elevated);
            padding: 16px 24px;
            border-radius: 12px;
            border: 2px solid #00A86B;
          }

          .soap-display {
            display: flex;
            align-items: center;
            gap: 16px;
          }

          .soap-info {
            display: flex;
            flex-direction: column;
          }

          .soap-mass {
            font-size: 20px;
            font-weight: 600;
            color: var(--text-primary);
          }

          .soap-label {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .action-buttons {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            justify-content: center;
          }

          .action-btn-group {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 20px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .action-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .action-btn.success:hover {
            background: #008B5B;
          }

          .safety-warning {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 16px;
            background: rgba(255, 193, 7, 0.1);
            border: 1px solid #FFC107;
            border-radius: 8px;
            color: #FFC107;
            font-size: 12px;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp5"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
    </LabBench>
  );
}
